import { type CSSProperties, type JSX, useState } from 'react';

import type { Grid } from '@schema/layout.js';
import type { Tile as TileSchema } from '@schema/tiles.js';

import { Tile } from './Tile.js';

export interface TileGridProps {
  grid: Grid;
  onTileActivate: (tile: TileSchema) => Promise<void> | void;
}

/**
 * Renders the tile grid using CSS Grid. Tiles fill cells in row-major order;
 * `spacer` tiles produce blanks. The grid is scrollable on overflow — Phase
 * 3 will replace this with proper Pro pagination / pageable swiping.
 */
export function TileGrid({ grid, onTileActivate }: TileGridProps): JSX.Element {
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [errorMap, setErrorMap] = useState<Record<string, string>>({});

  const handleActivate = async (tile: TileSchema): Promise<void> => {
    setPendingId(tile.id);
    setErrorMap((m) => {
      const { [tile.id]: _omit, ...rest } = m;
      void _omit;
      return rest;
    });
    try {
      await onTileActivate(tile);
    } catch (err) {
      setErrorMap((m) => ({
        ...m,
        [tile.id]: err instanceof Error ? err.message : String(err),
      }));
    } finally {
      setPendingId((current) => (current === tile.id ? null : current));
    }
  };

  return (
    <div
      className="tile-grid"
      style={
        // Columns flow through a CSS variable so a `@media
        // (orientation: landscape)` rule in styles/index.css can bump
        // them up — landscape screens (tablets, phones rotated) have
        // more horizontal room, and the wider config-default would
        // give awkwardly small tiles. The CSS rule overrides
        // --ml-tile-columns directly; portrait reads the config value.
        // Tile aspect-ratio: 1/1 (see CSS) keeps cells square; no
        // grid-level rows constraint, so overflow scrolls.
        { '--ml-tile-columns': String(grid.columns) } as CSSProperties
      }
      data-testid="tile-grid"
    >
      {grid.tiles.map((tile) => {
        const error = errorMap[tile.id];
        const pending = pendingId === tile.id;
        return (
          <Tile
            key={tile.id}
            tile={tile}
            onActivate={(t) => {
              void handleActivate(t);
            }}
            statusHint={error ?? (pending ? '…' : undefined)}
          />
        );
      })}
    </div>
  );
}
